import React, { useEffect, useState } from 'react';
import { useAuth } from '../context/AuthContext';
import apiService from '../services/ApiService';
import logo from '../assets/vaahanBuddyLogo.png';

const Login = () => {
  const { login } = useAuth();
  const [phone, setPhone] = useState('');
  const [otp, setOtp] = useState('');
  const [step, setStep] = useState(1);
  const [isLoading, setIsLoading] = useState(false);
  const [windowWidth, setWindowWidth] = useState(window.innerWidth);

  useEffect(() => {
    const handleResize = () => setWindowWidth(window.innerWidth);
    window.addEventListener('resize', handleResize);
    return () => window.removeEventListener('resize', handleResize);
  }, []);

  const isMobile = windowWidth < 768;
  const isTablet = windowWidth >= 768 && windowWidth < 1024;

  const handleSendOtp = async () => {
    if (!phone.match(/^\d{10}$/)) {
      alert('Please enter a valid 10-digit phone number');
      return;
    }
    setIsLoading(true);

    try {
      const result = await apiService.post('/users/select-type', {
        user_type: "admin",
        phone_number: phone
      });
      setStep(2);
    } catch (error) {
      alert(error.message || 'Failed to send OTP');
    } finally {
      setIsLoading(false);
    }
  };

  const handleLogin = async () => {
    if (otp.length !== 6) {
      alert('Please enter a 6-digit OTP');
      return;
    }
    setIsLoading(true);

    try {
      const result = await apiService.post('/users/verify-otp', {
        phone_number: phone,
        otp: otp
      });

      if (result.user_type !== 'admin' && result.user_type !== 'superadmin') {
        throw new Error('Access denied. Admin privileges required.');
      }

      login(result);
    } catch (error) {
      alert(error.message || 'Invalid OTP');
    } finally {
      setIsLoading(false);
    }
  };

  return (
    <div style={{
      display: 'flex',
      justifyContent: 'center',
      alignItems: 'center',
      minHeight: '100vh',
      background: 'var(--bg-main)',
      backgroundImage: `
        radial-gradient(at 0% 0%, rgba(79, 70, 229, 0.1) 0px, transparent 50%),
        radial-gradient(at 100% 0%, rgba(124, 58, 237, 0.1) 0px, transparent 50%),
        radial-gradient(at 100% 100%, rgba(16, 185, 129, 0.1) 0px, transparent 50%),
        radial-gradient(at 0% 100%, rgba(79, 70, 229, 0.1) 0px, transparent 50%)
      `,
      padding: '24px',
      boxSizing: 'border-box',
      position: 'relative',
      overflow: 'hidden'
    }}>
      <div style={{
        backgroundColor: 'rgba(255, 255, 255, 0.7)',
        backdropFilter: 'blur(24px)',
        WebkitBackdropFilter: 'blur(24px)',
        borderRadius: 'var(--radius-xl)',
        boxShadow: '0 25px 50px -12px rgba(0, 0, 0, 0.1)',
        padding: isMobile ? '32px 24px' : '60px 48px',
        width: '100%',
        maxWidth: '460px',
        textAlign: 'center',
        border: '1px solid rgba(255, 255, 255, 0.8)',
        zIndex: 1,
        position: 'relative'
      }}>

        <div style={{ marginBottom: '32px', display: 'flex', flexDirection: 'column', alignItems: 'center' }}>
          <img
            src={logo}
            alt="VaahanBuddy Logo"
            style={{
              maxHeight: '64px',
              width: 'auto',
              marginBottom: '16px',
              filter: 'drop-shadow(0 4px 10px rgba(0,0,0,0.1))'
            }}
          />
          <h1 style={{
            fontFamily: 'Outfit',
            fontSize: '28px',
            fontWeight: '800',
            color: 'var(--text-main)',
            margin: '0',
            letterSpacing: '-1px'
          }}>
            Welcome Back
          </h1>
          <p style={{
            fontSize: '14px',
            color: 'var(--text-muted)',
            marginTop: '8px'
          }}>
            Manage the VaahanBuddy ecosystem with ease.
          </p>
        </div>

        {step === 1 ? (
          <div style={{ textAlign: 'left' }}>
            <div style={{ marginBottom: '24px' }}>
              <label style={{
                display: 'block',
                marginBottom: '8px',
                fontWeight: '600',
                fontSize: '14px',
                color: 'var(--text-main)'
              }}>
                Phone Number
              </label>
              <div style={{ position: 'relative' }}>
                <i className="fas fa-phone" style={{
                  position: 'absolute',
                  left: '14px',
                  top: '50%',
                  transform: 'translateY(-50%)',
                  color: 'var(--text-dim)'
                }}></i>
                <input
                  type="tel"
                  value={phone}
                  onChange={(e) => setPhone(e.target.value.replace(/\D/g, '').slice(0, 10))}
                  placeholder="Enter 10-digit number"
                  style={{
                    width: '100%',
                    padding: '12px 12px 12px 40px',
                    borderRadius: 'var(--radius-md)',
                    border: '1px solid var(--border-light)',
                    fontSize: '16px',
                    boxSizing: 'border-box',
                    outline: 'none',
                    transition: 'all 0.2s',
                    background: 'white'
                  }}
                  onFocus={(e) => e.target.style.borderColor = 'var(--primary)'}
                  onBlur={(e) => e.target.style.borderColor = 'var(--border-light)'}
                />
              </div>
            </div>
            <button
              onClick={handleSendOtp}
              disabled={isLoading}
              className="btn btn-primary"
              style={{ width: '100%', height: '48px', fontSize: '16px' }}
            >
              {isLoading ? (
                <>
                  <i className="fas fa-circle-notch fa-spin"></i>
                  <span>Sending Code...</span>
                </>
              ) : (
                <>
                  <span>Get Security Code</span>
                  <i className="fas fa-arrow-right"></i>
                </>
              )}
            </button>
          </div>
        ) : (
          <div style={{ textAlign: 'left' }}>
            <div style={{ marginBottom: '24px' }}>
              <label style={{
                display: 'block',
                marginBottom: '8px',
                fontWeight: '600',
                fontSize: '14px',
                color: 'var(--text-main)'
              }}>
                Verification Code
              </label>
              <div style={{ position: 'relative' }}>
                <i className="fas fa-key" style={{
                  position: 'absolute',
                  left: '14px',
                  top: '50%',
                  transform: 'translateY(-50%)',
                  color: 'var(--text-dim)'
                }}></i>
                <input
                  type="text"
                  value={otp}
                  onChange={(e) => setOtp(e.target.value.replace(/\D/g, '').slice(0, 6))}
                  placeholder="Enter 6-digit OTP"
                  style={{
                    width: '100%',
                    padding: '12px 12px 12px 40px',
                    borderRadius: 'var(--radius-md)',
                    border: '1px solid var(--border-light)',
                    fontSize: '18px',
                    letterSpacing: '4px',
                    textAlign: 'center',
                    boxSizing: 'border-box',
                    outline: 'none',
                    transition: 'all 0.2s',
                    background: 'white'
                  }}
                  onFocus={(e) => e.target.style.borderColor = 'var(--primary)'}
                  onBlur={(e) => e.target.style.borderColor = 'var(--border-light)'}
                />
              </div>
              <p style={{
                fontSize: '12px',
                color: 'var(--text-muted)',
                marginTop: '12px',
                textAlign: 'center'
              }}>
                Code sent to <b>+91 {phone}</b>
              </p>
            </div>
            <button
              onClick={handleLogin}
              disabled={isLoading}
              className="btn btn-primary"
              style={{ width: '100%', height: '48px', fontSize: '16px', backgroundColor: '#10b981' }}
            >
              {isLoading ? (
                <>
                  <i className="fas fa-circle-notch fa-spin"></i>
                  <span>Verifying...</span>
                </>
              ) : (
                <>
                  <span>Verify & Login</span>
                  <i className="fas fa-check-circle"></i>
                </>
              )}
            </button>
            <button
              onClick={() => setStep(1)}
              style={{
                width: '100%',
                marginTop: '16px',
                background: 'transparent',
                border: 'none',
                color: 'var(--primary)',
                cursor: 'pointer',
                fontSize: '14px',
                fontWeight: '600',
                display: 'flex',
                alignItems: 'center',
                justifyContent: 'center',
                gap: '6px'
              }}
            >
              <i className="fas fa-edit"></i>
              <span>Change Number</span>
            </button>
          </div>
        )}
      </div>
    </div>
  );
};

export default Login;